import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "../../components/Billing/Billing.css";

// Paystack sends people back here after the payment page: /billing/callback?reference=...
// We ask the backend to check that reference with Paystack. Nothing here decides whether the payment counted.
export default function BillingCallback() {
  const [params] = useSearchParams();
  const reference = params.get("reference") || params.get("trxref") || "";
  const [state, setState] = useState(reference ? "checking" : "missing");   // checking | paid | unpaid | error | missing
  const [message, setMessage] = useState("");
  const asked = useRef(false);

  useEffect(() => {
    if (!reference || asked.current) return;
    asked.current = true;
    api.get("/api/billing/verify", { params: { reference } })
      .then((r) => setState(r.data.paid ? "paid" : "unpaid"))
      .catch((e) => {
        setMessage(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "We couldn't confirm your payment yet.");
        setState("error");
      });
  }, [reference]);

  let back = "/courses";
  try {
    const saved = sessionStorage.getItem("billing_return");
    if (saved && saved.startsWith("/") && !saved.startsWith("//")) back = saved;
  } catch { /* private mode: go to courses */ }

  return (
    <Layout>
      <div className="bl-page">
        <div className="bl-card bl-center" role="status" aria-live="polite">
          {state === "checking" && (<><div className="bl-spinner" /><h1>Confirming your payment…</h1><p>This only takes a moment. Please don't close this page.</p></>)}
          {state === "paid" && (
            <>
              <CheckCircle2 size={54} color="#16a34a" aria-hidden="true" />
              <h1>You're subscribed!</h1>
              <p>Thank you. Every subject and topic is now unlocked.</p>
              <div className="bl-actions" style={{ justifyContent: "center" }}>
                <Link to={back} className="bl-btn bl-btn-gold" onClick={() => { try { sessionStorage.removeItem("billing_return"); } catch { /* ignore */ } }}>Start learning</Link>
                <Link to="/subscribe" className="bl-btn bl-btn-outline">View subscription</Link>
              </div>
            </>
          )}
          {state === "unpaid" && (
            <>
              <AlertCircle size={54} color="#d9950f" aria-hidden="true" />
              <h1>The payment didn't go through</h1>
              <p>You haven't been charged. You can try again whenever you're ready.</p>
              <div className="bl-actions" style={{ justifyContent: "center" }}>
                <Link to="/subscribe" className="bl-btn bl-btn-gold">Try again</Link>
                <Link to="/courses" className="bl-btn bl-btn-outline">Back to courses</Link>
              </div>
            </>
          )}
          {(state === "error" || state === "missing") && (
            <>
              <AlertCircle size={54} color="#b91c1c" aria-hidden="true" />
              <h1>We couldn't confirm the payment</h1>
              <p>{state === "missing" ? "This page needs a payment reference." : message}</p>
              <p className="bl-note">If money left your account, don't worry: it is matched to you automatically within a few minutes. Check your subscription page shortly.</p>
              <div className="bl-actions" style={{ justifyContent: "center" }}>
                <Link to="/subscribe" className="bl-btn bl-btn-gold">Check my subscription</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
